import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { orgApi } from "../lib/organizerApi";

/**
 * Forgot-password — Phase 6.5 / Module 2.
 *
 * Public page. Submits an email; backend always returns a generic
 * "if it exists, we sent a link" message so we don't enumerate accounts.
 */
export default function OrganizerForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await orgApi.post("/organizer/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-forgot-page">
      <div className="w-full max-w-md">
        <Link to="/organizer/login" className="inline-flex items-center gap-1.5 text-espresso-soft hover:text-espresso text-sm">
          <ArrowLeft className="w-3.5 h-3.5" />
          Quay lại đăng nhập
        </Link>

        <div className="mt-6 bg-white border border-espresso/10 rounded-3xl p-7 sm:p-9 shadow-sm">
          <div className="flex items-center gap-2 text-espresso-soft text-xs uppercase tracking-[0.28em]">
            <Sparkles className="w-3.5 h-3.5" /> Khôi phục tài khoản
          </div>
          <h1 className="mt-2 text-3xl font-serif text-espresso">Quên mật khẩu?</h1>

          {done ? (
            <div data-testid="forgot-done" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">Đã gửi yêu cầu</div>
                  <p className="mt-1.5 text-sm text-emerald-800 leading-relaxed">
                    Nếu địa chỉ <strong>{email}</strong> tồn tại trong hệ thống,
                    chúng tôi đã gửi liên kết đặt lại mật khẩu. Liên kết có
                    hiệu lực trong <strong>60 phút</strong>. Vui lòng kiểm tra
                    cả hộp thư spam.
                  </p>
                </div>
              </div>
              <Link
                to="/organizer/login"
                data-testid="forgot-back-to-login"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-3 text-sm font-semibold"
              >
                Quay lại đăng nhập
              </Link>
            </div>
          ) : (
            <>
              <p className="mt-2 text-espresso-soft text-sm">
                Nhập email đã đăng ký. Chúng tôi sẽ gửi cho bạn một liên kết an toàn để đặt lại mật khẩu.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">Email</span>
                  <input
                    type="email"
                    data-testid="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="vidu@nailvietclub.com"
                    autoComplete="email"
                    className="mt-1 w-full bg-linen-100/60 border border-espresso/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
                  />
                </label>

                {error && (
                  <div data-testid="forgot-error" className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy || !email.trim()}
                  data-testid="forgot-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {busy ? "Đang gửi…" : "Gửi liên kết đặt lại"}
                </button>
              </form>

              <p className="mt-6 text-xs text-espresso-muted">
                Bạn chưa có tài khoản?{" "}
                <Link to="/organizer/signup" className="text-espresso underline-offset-2 hover:underline">Đăng ký miễn phí</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
